import typography from "../../styles/sbDesignSystem/sbTypography";
import {
  DateRange,
  DateRangeValidationError,
  LocalizationProvider,
  MultiInputDateRangeField,
  pickerDayClasses,
  pickersInputBaseClasses,
  PickersShortcutsItem,
  StaticDateRangePicker,
  StaticDateRangePickerSlotProps,
} from "@mui/x-date-pickers-pro";
import { AdapterDateFns } from "@mui/x-date-pickers-pro/AdapterDateFns";
import { subDays, subMonths } from "date-fns";
import { getCurrentUtcDate } from "../../lib/Util";
import { useBackfillStore } from "../../store/backfillsStore";

interface StaticDateRangePickerButtonProps {
  "data-testid": string;
}
interface StaticPickerSlotProps extends StaticDateRangePickerSlotProps {
  nextIconButton?: StaticDateRangePickerButtonProps;
  previousIconButton?: StaticDateRangePickerButtonProps;
}

const CalendarAndField = () => {
  const updateDate = useBackfillStore((state) => state.updateDate);
  const date = useBackfillStore((state) => state.date);
  const currentUtcDate = getCurrentUtcDate();

  const getShortcutsItems = (defaultStart?: Date, defaultEnd?: Date) => {
    const shortcutItems: PickersShortcutsItem<DateRange<Date>>[] = [
      {
        label: "Today",
        getValue: () => {
          return [currentUtcDate, currentUtcDate];
        },
      },
      {
        label: "Yesterday",
        getValue: () => {
          const yesterday = subDays(currentUtcDate, 1);
          return [yesterday, yesterday];
        },
      },
      {
        label: "7 Days",
        getValue: () => {
          return [subDays(currentUtcDate, 7), currentUtcDate];
        },
      },
      {
        label: "30 Days",
        getValue: () => {
          return [subDays(currentUtcDate, 30), currentUtcDate];
        },
      },
      {
        label: "3 Months",
        getValue: () => {
          return [subMonths(currentUtcDate, 3), currentUtcDate];
        },
      },
      {
        label: "6 Months",
        getValue: () => {
          return [subMonths(currentUtcDate, 6), currentUtcDate];
        },
      },
    ];

    if (defaultStart && defaultEnd) {
      shortcutItems.push({
        label: "Reset",
        getValue: () => {
          return [defaultStart, defaultEnd];
        },
      });
    }

    return shortcutItems;
  };
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <MultiInputDateRangeField
        value={[date.start, date.end]}
        onChange={(value) => {
          if (value[0] && value[1]) {
            updateDate({ start: value[0], end: value[1] });
          }
        }}
        sx={{
          mb: "8px",
          [`& .${pickersInputBaseClasses.root}`]: {
            width: "192px",
            ...(typography && typography["body.medium.regular"]),
            height: "40px",
          },
        }}
        disableFuture
        maxDate={getCurrentUtcDate()}
      />
      <StaticDateRangePicker
        value={[date.start, date.end]}
        displayStaticWrapperAs="desktop"
        calendars={1}
        onChange={(value: DateRange<Date>, context: { validationError: DateRangeValidationError }) => {
          if (context.validationError[1] === "disableFuture") {
            value[1] = currentUtcDate;
          }
          if (value[0] && value[1]) {
            updateDate({ start: value[0], end: value[1] });
          }
        }}
        slots={{
          actionBar: () => null,
        }}
        slotProps={
          {
            toolbar: {
              toolbarFormat: "MM/dd/yyyy",
            },
            layout: {
              sx: {
                "& .MuiPickersCalendarHeader-label": {
                  fontSize: "12px",
                },
                "& .MuiDayCalendar-weekDayLabel": {
                  fontSize: "12px",
                },
                "& .MuiChip-filled.MuiChip-colorDefault": {
                  fontSize: "10px",
                },
                [`& .${pickerDayClasses.selected}`]: {
                  color: "text10.main",
                },
              },
            },
            day: {
              sx: {
                "& .MuiButtonBase-root": {
                  fontSize: "12px",
                },
              },
            },
            shortcuts: {
              items: getShortcutsItems(currentUtcDate, currentUtcDate),
            },
            nextIconButton: {
              "data-testid": "arrow-right-icon",
            },
            previousIconButton: {
              "data-testid": "arrow-left-icon",
            },
          } as StaticPickerSlotProps
        }
        maxDate={currentUtcDate}
        disableFuture
      />
    </LocalizationProvider>
  );
};

export default CalendarAndField;
